export type SentimentType =
  | 'positive'
  | 'negative'
  | 'neutral'
  | 'spam'
  | 'purchase_intent'
  | 'question'
  | 'complaint'
  // Non-product revenue opportunity: booking, speaking, collaboration,
  // brand/sponsorship deal, partnership, sales lead (Slice 2 money signals).
  | 'lead';
// 'review' is a Google Business Profile review (Slice 3) — a distinct shape with a
// star_rating and an optionally-empty message_text (rating-only reviews). It routes
// to a dedicated ReviewCard, not MessageCard.
export type EngagementItemType = 'comment' | 'dm' | 'story_reply' | 'review';
// 'sending' is the transient claim the send-engagement-reply edge fn writes
// before the irreversible platform POST (the atomic double-send guard, Slice 1f);
// it must be in the union so client code that reads a mid-send row is type-safe.
export type EngagementStatus = 'unread' | 'read' | 'replied' | 'hidden' | 'flagged' | 'sending';
// 'google-business' = Google Business Profile (Slice 3, premium surface). Matches the
// platform string the GBP OAuth callback + user_connections use. 'linkedin' is GATED —
// the fetch path is inert until LinkedIn Marketing Developer Platform read scopes are
// approved (see fetch-engagement), but the type allows it so the UI can render it.
export type EngagementPlatform = 'instagram' | 'facebook' | 'google-business' | 'linkedin';

// Coarse kind for a DM media attachment so the card can pick a thumbnail vs a chip.
export type AttachmentType = 'image' | 'video' | 'audio' | 'file' | 'share';

export interface EngagementItem {
  id: string;
  user_id: string;
  // The workspace this item's platform connection belongs to. Stamped at
  // ingestion (fetch-engagement / webhook dual-write) going forward; NULL on
  // a legacy row genuinely too ambiguous to backfill (2026-08-28 fix).
  brand_kit_id: string | null;
  platform: EngagementPlatform;
  item_type: EngagementItemType;
  external_id: string;
  author_name: string | null;
  author_username: string | null;
  author_avatar_url: string | null;
  message_text: string;
  post_id: string | null;
  post_external_id: string | null;
  post_permalink: string | null;
  post_caption_preview: string | null;
  parent_comment_id: string | null;
  sentiment: SentimentType | null;
  // 1–5 stars for a Google Business Profile review (item_type === 'review'); null for
  // every other item type. Drives the ReviewCard star display + low-star treatment.
  star_rating: number | null;
  detected_intent: string | null;
  suggested_reply: string | null;
  confidence_score: number | null;
  detected_order_number: string | null;
  // Title of a Shopify product the message references (matched against the user's
  // catalog at ingest). Drives the product-context chip; null when none detected.
  detected_product_title: string | null;
  // Media attached to a DM (photo/video/file/shared post). Lets media-only messages
  // surface instead of being dropped. Both null for text-only items.
  attachment_url: string | null;
  attachment_type: AttachmentType | null;
  shopify_order_data: ShopifyOrderData | null;
  status: EngagementStatus;
  replied_text: string | null;
  replied_at: string | null;
  platform_created_at: string;
  created_at: string;
  updated_at: string;
}

export interface ShopifyOrderData {
  name: string;
  status: string;
  fulfillment_status: string;
  tracking_number?: string;
  tracking_url?: string;
  tracking_company?: string;
  total_price: string;
  currency: string;
  created_at: string;
  line_items: Array<{ title: string; quantity: number }>;
}

export interface EngagementStats {
  total: number;
  unread: number;
  comments: number;
  dms: number;
  /** Google Business Profile reviews. */
  reviews: number;
  flagged: number;
  /** Active items whose sentiment demands a human response (the "Needs you" view). */
  needsYou: number;
  /** Replied/completed items, surfaced under the "Done" tab. */
  done: number;
  /** Dismissed items, recoverable from the "Hidden" tab. */
  hidden: number;
}

/**
 * Single source of truth for sentiment urgency on the client. This MUST mirror
 * the DB generated column `engagement_items.sentiment_priority`
 * (migration `20260616140000_engagement_items_sentiment_priority.sql`) — lower =
 * more urgent. The two can't share code across the TS/SQL boundary, so the
 * `engagement priority ladder` test pins these values: if you change a priority
 * here (or add a sentiment), that test fails and reminds you to update the
 * migration's CASE too. (Unclassified/NULL maps to 7 in the DB; there is no
 * SentimentType literal for it.)
 */
export const SENTIMENT_PRIORITY: Record<SentimentType, number> = {
  complaint: 1,
  negative: 2,
  purchase_intent: 3,
  // Money signals tie with purchase_intent at the top — both are revenue.
  lead: 3,
  question: 4,
  positive: 5,
  neutral: 6,
  spam: 8,
};

/** The DB priority band (<= this) that the "Needs you" view surfaces. */
export const NEEDS_YOU_PRIORITY_MAX = 4;

/**
 * Sentiments that put an item in the "Needs you" view: an unhappy customer
 * (complaint/negative), a buying signal (purchase_intent), or an unanswered
 * question. Derived from SENTIMENT_PRIORITY so it can never drift from the
 * ranking — change a priority and this set follows automatically.
 */
export const NEEDS_YOU_SENTIMENTS = (
  Object.entries(SENTIMENT_PRIORITY) as Array<[SentimentType, number]>
)
  .filter(([, priority]) => priority <= NEEDS_YOU_PRIORITY_MAX)
  .map(([sentiment]) => sentiment);

export interface EngagementFilters {
  platform?: string;
  type?: string;
  status?: string;
  /** Include already-replied items in the result (for the "Done" tab). Default
   *  false keeps the triage inbox to open items only. */
  includeReplied?: boolean;
  /** Include hidden items for the recoverable "Hidden" tab. Default false. */
  includeHidden?: boolean;
  /** Active workspace kit id — scopes the pooled account-wide table to this
   *  workspace's own attributed rows plus true legacy-NULL rows. */
  activeBrandKitId?: string | null;
}
